import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Drop, ArrowCounterClockwise } from "@phosphor-icons/react";

const PORTIONS = [100, 200, 250, 300, 500];

export const WaterCard = ({ date, refreshKey, onEditNorm }) => {
  const [data, setData] = useState(null);
  const [portion, setPortion] = useState(250);
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/nutrition/water?date=${date}`).then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, [date, refreshKey]);

  const add = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post("/nutrition/water", { ml: portion, date });
      setData((d) => ({ ...d, total: r.total, entries: [...d.entries, { id: r.id, ml: portion }] }));
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const undo = async () => {
    const last = data?.entries[data.entries.length - 1];
    if (!last) return;
    setData((d) => ({ ...d, total: d.total - last.ml, entries: d.entries.slice(0, -1) }));
    try { await api.delete(`/nutrition/water/${last.id}`); } catch { load(); }
  };

  if (!data) return null;
  const pct = Math.min(100, Math.round((data.total / data.norm_ml) * 100));
  const doneNorm = data.total >= data.norm_ml;

  return (
    <div className="relative overflow-hidden rounded-2xl border border-white/[0.08] mb-4" data-testid="water-card">
      <img src="/water-hero.jpg" alt="" className="absolute inset-0 w-full h-full object-cover object-right opacity-70" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#060a10] via-[#060a10]/85 to-[#060a10]/20" />
      <div className="relative p-5">
        <div className="flex items-center gap-2 mb-1">
          <Drop size={18} weight="fill" className="text-cyan-400" />
          <span className="font-heading font-semibold">Вода</span>
          {doneNorm && <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-cyan-400/15 text-cyan-300 ring-1 ring-cyan-400/25" data-testid="water-done-badge">✓ норма выпита</span>}
        </div>
        <div className="flex items-baseline gap-1.5">
          <span className="font-heading text-4xl font-extrabold num text-cyan-300" data-testid="water-total">{data.total}</span>
          <span className="text-sm text-muted-foreground num">/ {data.norm_ml} мл</span>
        </div>
        <button onClick={onEditNorm} data-testid="water-norm-label"
          className="text-[11px] text-muted-foreground hover:text-foreground underline decoration-dotted underline-offset-2 mt-0.5 transition-colors text-left">
          {data.weight_kg
            ? `норма 35 мл × ${data.weight_kg} кг${data.height_cm ? ` · рост ${data.height_cm} см` : ""} · изменить`
            : "укажите рост и вес — норма посчитается точнее"}
        </button>
        <div className="h-2.5 rounded-full bg-white/[0.08] overflow-hidden mt-3 max-w-md">
          <div className="h-full rounded-full bg-gradient-to-r from-cyan-500 to-blue-400 transition-all duration-700"
            style={{ width: `${pct}%` }} data-testid="water-progress" />
        </div>
        <div className="flex items-center gap-1.5 mt-4 flex-wrap">
          {PORTIONS.map((p) => (
            <button key={p} onClick={() => setPortion(p)} data-testid={`water-portion-${p}`}
              className={`px-3 py-1.5 rounded-full text-xs font-bold num transition-colors ${portion === p
                ? "bg-cyan-400 text-black" : "bg-white/[0.07] text-muted-foreground hover:text-foreground"}`}>
              {p}
            </button>
          ))}
          <span className="text-[10px] text-muted-foreground ml-0.5">мл</span>
        </div>
        <div className="flex items-center gap-2 mt-3">
          <button onClick={add} disabled={busy} data-testid="water-add-btn"
            className="flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-bold text-base text-black
                       bg-gradient-to-r from-cyan-400 to-blue-400 hover:brightness-110 active:scale-[0.98]
                       transition-all shadow-lg shadow-cyan-500/25 disabled:opacity-50">
            <Drop size={20} weight="fill" /> Прибавить +{portion} мл
          </button>
          {data.entries.length > 0 && (
            <button onClick={undo} data-testid="water-undo-btn" title="Отменить последнюю порцию"
              className="p-3 rounded-xl bg-white/[0.07] text-muted-foreground hover:text-foreground transition-colors">
              <ArrowCounterClockwise size={18} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
